import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Loader2, Pause, WifiOff, Phone } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { SuperAdminService, RemoteSession } from '../../services/superAdminService';

type RRWebEvent = Record<string, unknown>;

const RRWEB_CDN_JS = 'https://cdn.jsdelivr.net/npm/rrweb@2.0.0-alpha.13/dist/rrweb.min.js';
const RRWEB_CDN_CSS = 'https://cdn.jsdelivr.net/npm/rrweb@2.0.0-alpha.13/dist/rrweb.min.css';
const RRWEB_LOAD_TIMEOUT_MS = 15_000;

/** Load rrweb Replayer at runtime. No crossOrigin attr to avoid CORS issues
 *  in Capacitor WKWebView (origin is capacitor://localhost). */
function loadRRWebOnce(): Promise<{ Replayer: new (events: RRWebEvent[], config: Record<string, unknown>) => { addEvent: (ev: RRWebEvent) => void; play: () => void } }> {
  return new Promise((resolve, reject) => {
    const w = window as Window & { rrweb?: any; __rrweb?: any };
    const existing = w.__rrweb ?? w.rrweb;
    if (existing?.Replayer) {
      resolve(existing);
      return;
    }

    if (!document.querySelector('link[data-rrweb-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = RRWEB_CDN_CSS;
      link.setAttribute('data-rrweb-css', '1');
      document.head.appendChild(link);
    }

    const timeout = setTimeout(() => {
      script.onload = null;
      script.onerror = null;
      script.remove();
      reject(new Error('rrweb script load timed out'));
    }, RRWEB_LOAD_TIMEOUT_MS);

    const script = document.createElement('script');
    script.src = RRWEB_CDN_JS;
    script.onload = () => {
      clearTimeout(timeout);
      const mod = w.__rrweb ?? w.rrweb;
      if (mod?.Replayer) {
        w.__rrweb = mod;
        resolve(mod);
      } else {
        reject(new Error('rrweb did not expose Replayer'));
      }
    };
    script.onerror = () => {
      clearTimeout(timeout);
      script.remove();
      reject(new Error('Failed to load rrweb script'));
    };
    document.head.appendChild(script);
  });
}

async function loadRRWeb(): Promise<{ Replayer: new (events: RRWebEvent[], config: Record<string, unknown>) => { addEvent: (ev: RRWebEvent) => void; play: () => void } }> {
  try {
    return await loadRRWebOnce();
  } catch (firstErr) {
    console.warn('rrweb first load attempt failed, retrying…', firstErr);
    await new Promise(r => setTimeout(r, 1500));
    return loadRRWebOnce();
  }
}

interface RemoteAccessViewerProps {
  session: RemoteSession;
  onClose: () => void;
}

export function RemoteAccessViewer({ session, onClose }: RemoteAccessViewerProps) {
  const [status, setStatus] = useState<RemoteSession['status']>(session.status);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [eventBuffer, setEventBuffer] = useState<RRWebEvent[]>([]);
  const [contentScale, setContentScale] = useState(1);
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
  const replayerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const replayerInitialized = useRef(false);

  const initReplayer = useCallback(async (events: RRWebEvent[]) => {
    if (!containerRef.current || replayerInitialized.current || events.length === 0) return;

    try {
      const rrweb = await loadRRWeb();

      if (containerRef.current) {
        containerRef.current.innerHTML = '';

        const replayer = new rrweb.Replayer(events, {
          root: containerRef.current,
          live: true,
          liveStartIndex: 0,
          liveDelay: 100,
          insertStyleRules: [
            '.replayer-wrapper iframe { border: none !important; }',
          ],
        });

        const firstTimestamp = (events[0] as any)?.timestamp;
        replayer.startLive(firstTimestamp);
        replayerRef.current = replayer;
        replayerInitialized.current = true;

        // Measure the recorded viewport and scale to fill available space
        requestAnimationFrame(() => {
          const wrapper = containerRef.current?.querySelector('.replayer-wrapper') as HTMLElement | null;
          if (wrapper) {
            const vw = wrapper.offsetWidth || 390;
            const vh = wrapper.offsetHeight || 844;
            const maxH = window.innerHeight - 120;
            const maxW = Math.min(window.innerWidth - 40, 420);
            const scale = Math.min(maxW / vw, maxH / vh);
            setContentScale(scale);
            setFrameSize({ width: Math.round(vw * scale), height: Math.round(vh * scale) });
          }
        });
      }
    } catch (error) {
      console.error('Failed to initialize rrweb replayer:', error);
    }
  }, []);

  const addEvents = useCallback((newEvents: RRWebEvent[]) => {
    if (replayerRef.current && replayerInitialized.current) {
      newEvents.forEach((event) => {
        try {
          replayerRef.current.addEvent(event);
        } catch (err) {
          // Full snapshot (checkout) may require replayer rebuild
          const evType = (event as any)?.type;
          if (evType === 2) {
            console.warn('Replayer failed on full snapshot, rebuilding...', err);
          }
        }
      });
    } else {
      setEventBuffer((prev) => {
        const combined = [...prev, ...newEvents];
        if (!replayerInitialized.current && combined.length >= 2) {
          initReplayer(combined);
        }
        return combined;
      });
    }
  }, [initReplayer]);

  useEffect(() => {
    let disposed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function subscribeBroadcast() {
      if (disposed) return;
      const channel = supabase.channel(`remote-session:${session.id}`, {
        config: { broadcast: { self: false } },
      });

      channel
        .on('broadcast', { event: 'rrweb_events' }, (payload) => {
          if (payload.payload?.events) {
            addEvents(payload.payload.events);
          }
        })
        .on('broadcast', { event: 'session_status' }, (payload) => {
          const newStatus = payload.payload?.status;
          if (newStatus === 'error') {
            setErrorMessage(payload.payload?.message || 'Recording failed on user device');
            setStatus('ended');
          } else if (newStatus) {
            setStatus(newStatus);
          }
        })
        .subscribe((subscribedStatus) => {
          if (subscribedStatus === 'CHANNEL_ERROR' || subscribedStatus === 'TIMED_OUT') {
            console.warn('[RemoteViewer] Broadcast channel error, reconnecting in 2s…');
            channel.unsubscribe();
            if (!disposed) {
              reconnectTimer = setTimeout(subscribeBroadcast, 2000);
            }
          }
        });

      channelRef.current = channel;
    }

    subscribeBroadcast();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      channelRef.current?.unsubscribe();
      channelRef.current = null;
    };
  }, [session.id, addEvents]);

  // Also listen for DB changes to the session row (with reconnect)
  useEffect(() => {
    let disposed = false;
    let dbChannelRef: ReturnType<typeof supabase.channel> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function subscribeDB() {
      if (disposed) return;
      const dbChannel = supabase
        .channel(`remote-session-db:${session.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'remote_sessions',
            filter: `id=eq.${session.id}`,
          },
          (payload) => {
            const newStatus = payload.new?.status as RemoteSession['status'];
            if (newStatus) setStatus(newStatus);
          }
        )
        .subscribe((subscribedStatus) => {
          if (subscribedStatus === 'CHANNEL_ERROR' || subscribedStatus === 'TIMED_OUT') {
            console.warn('[RemoteViewer] DB channel error, reconnecting in 2s…');
            dbChannel.unsubscribe();
            if (!disposed) {
              reconnectTimer = setTimeout(subscribeDB, 2000);
            }
          }
        });

      dbChannelRef = dbChannel;
    }

    subscribeDB();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      dbChannelRef?.unsubscribe();
    };
  }, [session.id]);

  const handleEndSession = async () => {
    await SuperAdminService.endRemoteSession(session.id, 'admin');
    if (channelRef.current) {
      channelRef.current.send({
        type: 'broadcast',
        event: 'session_status',
        payload: { status: 'ended' },
      });
    }
    onClose();
  };

  useEffect(() => {
    return () => {
      if (replayerRef.current) {
        try {
          replayerRef.current.pause?.();
          replayerRef.current = null;
        } catch {
          // Cleanup error, ignore
        }
      }
      replayerInitialized.current = false;
    };
  }, []);

  const isTerminated = status === 'ended' || status === 'rejected' || status === 'expired';
  const isLive = status === 'active';
  const showScreen = status === 'approved' || status === 'active' || status === 'paused';

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm">
      {/* Compact top bar */}
      <div className="flex items-center gap-3 mb-3">
        {/* Status pill */}
        <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-3 py-1.5">
          {isLive && (
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
          )}
          {status === 'pending' && <Loader2 className="h-3 w-3 text-amber-400 animate-spin" />}
          {status === 'approved' && <Loader2 className="h-3 w-3 text-blue-400 animate-spin" />}
          {status === 'paused' && <Pause className="h-3 w-3 text-orange-400" />}
          {isTerminated && <WifiOff className="h-3 w-3 text-gray-400" />}
          <span className="text-white/90 text-xs font-medium">
            {status === 'pending' && 'Waiting for approval…'}
            {status === 'approved' && 'Connecting…'}
            {isLive && 'Live'}
            {status === 'paused' && 'Paused'}
            {status === 'ended' && 'Session ended'}
            {status === 'rejected' && 'Access denied'}
            {status === 'expired' && 'Expired'}
          </span>
        </div>

        {/* Session code */}
        <div className="bg-white/10 backdrop-blur rounded-full px-2.5 py-1.5">
          <span className="text-white/60 font-mono text-[11px] tracking-wider">{session.session_code}</span>
        </div>

        {/* End / Close */}
        {!isTerminated ? (
          <button
            onClick={handleEndSession}
            className="bg-red-500/90 hover:bg-red-500 text-white text-xs font-medium rounded-full px-3.5 py-1.5 transition-colors"
          >
            End Session
          </button>
        ) : (
          <button
            onClick={onClose}
            className="bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-full px-3.5 py-1.5 transition-colors"
          >
            Close
          </button>
        )}

        {/* X button */}
        <button
          onClick={onClose}
          className="bg-white/10 hover:bg-white/20 text-white/70 hover:text-white rounded-full p-1.5 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Screen area */}
      <div className="relative">
        {/* Non-live states: centered messages */}
        {status === 'pending' && (
          <div className="flex flex-col items-center justify-center py-32">
            <div className="h-16 w-16 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4">
              <Phone className="h-8 w-8 text-amber-400 animate-pulse" />
            </div>
            <p className="text-white font-medium text-sm mb-1">Requesting Access</p>
            <p className="text-white/50 text-xs">Waiting for the user to approve…</p>
          </div>
        )}

        {status === 'rejected' && (
          <div className="flex flex-col items-center justify-center py-32">
            <div className="h-16 w-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4">
              <X className="h-8 w-8 text-red-400" />
            </div>
            <p className="text-white font-medium text-sm mb-1">Access Denied</p>
            <p className="text-white/50 text-xs">The user declined the request.</p>
          </div>
        )}

        {(status === 'expired' || status === 'ended') && (
          <div className="flex flex-col items-center justify-center py-32">
            <div className="h-16 w-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4">
              <WifiOff className="h-8 w-8 text-white/40" />
            </div>
            <p className="text-white font-medium text-sm mb-1">
              {errorMessage ? 'Recording Error' : status === 'ended' ? 'Session Ended' : 'Request Expired'}
            </p>
            <p className="text-white/50 text-xs">
              {errorMessage || (status === 'ended' ? 'The remote session has been terminated.' : 'No response was received in time.')}
            </p>
          </div>
        )}

        {/* Live screen frame — sized exactly to the recorded viewport */}
        {showScreen && (
          <div
            className="relative overflow-hidden rounded-2xl"
            style={{
              width: frameSize.width || 300,
              height: frameSize.height || 650,
              boxShadow: '0 0 0 1px rgba(255,255,255,0.08), 0 25px 60px -12px rgba(0,0,0,0.5)',
              transition: 'width 0.3s ease, height 0.3s ease',
            }}
          >
            <div style={{ transform: `scale(${contentScale})`, transformOrigin: 'top left' }}>
              <div
                ref={containerRef}
                className="bg-gray-950"
                style={{ width: 500, height: 1000 }}
              />
            </div>

            {/* Paused overlay */}
            {status === 'paused' && (
              <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-20">
                <div className="text-center">
                  <Pause className="h-8 w-8 text-white/80 mx-auto mb-2" />
                  <p className="text-white font-medium text-sm">Paused</p>
                  <p className="text-white/50 text-[11px]">App is minimized</p>
                </div>
              </div>
            )}

            {/* Connecting overlay */}
            {status === 'approved' && !replayerInitialized.current && (
              <div className="absolute inset-0 bg-gray-950 flex items-center justify-center z-20">
                <div className="text-center">
                  <Loader2 className="h-6 w-6 text-white/60 animate-spin mx-auto mb-2" />
                  <p className="text-white/50 text-xs">Connecting…</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Subtle footer text */}
      {showScreen && (
        <p className="text-white/30 text-[10px] mt-3">View-only · User is in control</p>
      )}
    </div>
  );
}
